import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import {
  Input,
  MultiSelectDropdown,
  Option,
} from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { addBulkShifts, dateToISO } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import { addDays, addHours, getISOWeek, isSameWeek,isValid } from "date-fns";
import { useEffect,useState } from "react";
import { DateRange } from "react-day-picker";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface NewOpenShiftWeeklyModalProps extends ModalProps {
  selectedWeek: DateRange | undefined;
  companyId: string;
  companyLocationId: string;
}

export function NewOpenShiftWeeklyModal({
  selectedWeek,
  companyId,
  companyLocationId,
  ...props
}: NewOpenShiftWeeklyModalProps) {
  const { t, i18n } = useTranslation();
  // const teamOptions: Option[] = [
  //   { value: 1, label: "Team 1" },
  //   { value: 2, label: "Team 2" },
  //   { value: 3, label: "Team 3" },
  //   { value: 4, label: "Team 4" },
  //   { value: 5, label: "Team 5" },
  // ];

  const [days, setDays] = useState<Option[]>([]);
  // calculated time is a float number. e.g. 6.30 = 6.30 * 60 Minutes, 0.5 = 0.5 * 60 = 30 Minutes
  const [calculatedTime, setCalculatedTime] = useState(0);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const processDays = () => {
    const isNextDay = getHoursInNumber(endTime) < getHoursInNumber(startTime);
    return days.map((day) => {
      const dayIndex = parseInt(day?.value.toString());
      return {
        plannedStartTime: dateToISO(
          addHours(
            addDays(selectedWeek?.from as Date, dayIndex - 1),
            getHoursInNumber(startTime),
          ),
        ),
        plannedEndTime: dateToISO(
          addHours(
            addDays(
              selectedWeek?.from as Date,
              dayIndex + (isNextDay ? 0 : -1),
            ),
            getHoursInNumber(endTime),
          ),
        ),
      };
    });
  };
  const newWeeklyOpenShiftSchema = z.object({
    startTime: z.string().min(1, t("shift.Start time is required")),
    endTime: z.string().min(1, t("shift.End time is required")),
    days: z.boolean().refine((value) => value, {
      message: t("shift.At least one day is required"),
    }),
    numberOfOpenShifts: z.string().min(1),
  });
  type NewWeeklyOpenShiftData = z.infer<typeof newWeeklyOpenShiftSchema>;

  const onSubmit = (data: NewWeeklyOpenShiftData) => {
    const shiftDates = processDays();
    const shiftDatesPayload: {
      plannedStartTime: string;
      plannedEndTime: string;
    }[] = [];

    for (let i = 0; i < Number(data.numberOfOpenShifts); i++) {
      shiftDatesPayload.push(...shiftDates);
    }

    const finalPayload = {
      companyId,
      companyLocationId,
      users: [],
      shiftDates: shiftDatesPayload,
    };
    mutation.mutate(finalPayload);
  };

  const queryClient = useQueryClient();
  const {
    setValue,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<NewWeeklyOpenShiftData>({
    resolver: zodResolver(newWeeklyOpenShiftSchema),
    defaultValues: {
      days: false,
    },
  });

  const mutation = useMutation({
    mutationFn: addBulkShifts,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shifts", companyLocationId],
      });
      setStartTime("");
      setEndTime("");
      setDays([]);
      reset();
      props.onClose?.();
      toast.success(t("shift.Shifts have been planned"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const dayOptions: Option[] = [
    { value: 1, label: t("shift.Monday") },
    { value: 2, label: t("shift.Tuesday") },
    { value: 3, label: t("shift.Wednesday") },
    { value: 4, label: t("shift.Thursday") },
    { value: 5, label: t("shift.Friday") },
    { value: 6, label: t("shift.Saturday") },
    { value: 7, label: t("shift.Sunday") },
  ];

  const currentDate = new Date();
  let availableDayOptions = dayOptions;

  if (selectedWeek?.from) {
    if (isSameWeek(currentDate, selectedWeek.from, { weekStartsOn: 1 })) {
      // Selected week is the current week
      const currentDayNumber = currentDate.getDay();
      const adjustedCurrentDayNumber =
        currentDayNumber === 0 ? 7 : currentDayNumber;

      availableDayOptions = dayOptions.filter(
        (option) => (option.value as number) > adjustedCurrentDayNumber,
      );
    }
  }
  const getHoursInNumber = (time: string): number => {
    if (!time) return 0;
    const [hour, minute] = time.split(":").map(Number);
    return hour + minute / 60;
  };

  const calculateTimeDifference = (start: string, end: string): number => {
    if (!start || !end) return 0.0;

    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0);

    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return parseFloat(diffInHours.toFixed(2));
  };

  useEffect(() => {
    setValue("days", days.length > 0);
  }, [days, setValue]);

  useEffect(() => {
    const timeDiff = calculateTimeDifference(startTime, endTime);
    setCalculatedTime(timeDiff);
  }, [startTime, endTime]);

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={() => props.onClose?.()}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("shift.Plan Shifts")}
          </Button>
        </>
      }
      {...props}
      title={t("shift.Plan New Shifts to Week", {
        week: getISOWeek(selectedWeek?.from ?? ""),
        fromDate: isValid(selectedWeek?.from)
          ? Intl.DateTimeFormat(i18n.language, {
              day: "2-digit",
              month: "short",
            }).format(selectedWeek?.from)
          : t("shift.Invalid Date"),
        toDate: isValid(selectedWeek?.to)
          ? Intl.DateTimeFormat(i18n.language, {
              day: "2-digit",
              month: "short",
            }).format(selectedWeek?.to)
          : t("shift.Invalid Date"),
      })}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label title={t("shift.Select Days")}>
          <div className="flex flex-col gap-y-1 w-full">
            <MultiSelectDropdown
              variant="filled"
              placeholder={t("shift.Select Days")}
              options={availableDayOptions}
              selectedOptions={days}
              onSelect={(selected) => setDays(selected)}
            />
            <ErrorMessage message={errors?.days?.message} />
          </div>
        </Label>
        <Label title={t("shift.Shift Beginning and End")}>
          <div className="flex flex-row justify-between w-full gap-2">
            <div className="flex flex-col w-full gap-y-1">
              <Input
                {...register("startTime")}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                type="time"
              />
              <ErrorMessage message={errors?.startTime?.message} />
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <Input
                {...register("endTime")}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                type="time"
              />
              <ErrorMessage message={errors?.endTime?.message} />
            </div>
          </div>
        </Label>
        {/* <Label title={t("shift.Team Restrictions")}>
          <Select
            options={teamOptions}
            placeholder="Soon..."
            size="md"
            disabled
          />
        </Label> */}
        <Label title={t("shift.Number of Open Shifts")}>
          <Input
            type="number"
            defaultValue={1}
            {...register("numberOfOpenShifts")}
          />
          <ErrorMessage message={errors?.numberOfOpenShifts?.message} />
        </Label>
        <Label title={t("shift.Calculated Time")}>
          <Input value={calculatedTime} disabled />
        </Label>
      </form>
    </Modal>
  );
}
